"use client";

import { useState } from "react";
import Image from "next/image";
import {
  BlockWrapper,
  Button,
  ButtonGroup,
  H2,
  MarkdownRenderer,
  SectionTag,
} from "@/shared/components";
import type { ButtonDataType, ImageDataType } from "@/shared/types";
import type { TableBlockTable } from "@/lib/directus/utils/getTableBlockRows";
import type { CommonBlockProps } from "./common/types";
import {
  getMarkdownProseClass,
  getSectionBorderClass,
  getSectionContentPaddingClass,
  getSectionHeaderMobilePaddingClass,
  getSectionSplitLeadingClass,
  getSectionTaglineSidebarClass,
  sectionBgToWrapper,
} from "./blockSection";
import {
  ORDER_FORM_ANCHOR_ID,
  scrollToOrderForm,
} from "./orderFormAnchor";
import { resolveTableTheme } from "./tableTheme";
import { getSharedTableColumnMinWidths } from "./tableColumnWidths";
import { TableBlockTableSection } from "./TableBlockTableSection";
import { cn } from "@/lib/utils";

interface TableBlockProps extends CommonBlockProps {
  tables: TableBlockTable[];
  caption?: string | null;
  showCta?: boolean;
  ctaImage?: ImageDataType | null;
  ctaHeadline?: string | null;
  ctaDescription?: string | null;
  ctaButtons?: ButtonDataType[];
}

const ctaImageColumnClass = cn(
  "relative w-full shrink-0 overflow-hidden",
  "min-h-[280px] md:min-h-[350px]",
  "desktop:w-1/3 desktop:min-h-full desktop:self-stretch"
);

const ctaMarkdownProseClass =
  "font-sans text-base leading-[1.2] [&_a]:text-link [&_li]:mt-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_p+p]:mt-6 [&_ul]:list-disc [&_ul]:pl-6";

function isOrderFormAnchorHref(href: string): boolean {
  const normalizedHref = href.trim();
  return (
    normalizedHref === `#${ORDER_FORM_ANCHOR_ID}` ||
    normalizedHref === ORDER_FORM_ANCHOR_ID
  );
}

function TableCtaButtonGroup({ buttons }: { buttons: ButtonDataType[] }) {
  if (buttons.length === 0) {
    return null;
  }

  const anchorButtons = buttons.filter((button) =>
    isOrderFormAnchorHref(button.href)
  );
  const linkButtons = buttons.filter(
    (button) => !isOrderFormAnchorHref(button.href)
  );

  return (
    <div className="flex flex-row flex-wrap gap-6">
      {anchorButtons.map((button, index) => (
        <Button
          key={`anchor-${index}`}
          type="button"
          variant={button.variant ?? "default"}
          size={button.size}
          onClick={() => {
            scrollToOrderForm();
          }}
        >
          {button.children}
        </Button>
      ))}
      {linkButtons.length > 0 && (
        <ButtonGroup items={linkButtons} className="flex-wrap" />
      )}
    </div>
  );
}

export function TableBlock({
  headline,
  tagline,
  tables,
  caption,
  sectionBg = "light",
  showCta = false,
  ctaImage = null,
  ctaHeadline = null,
  ctaDescription = null,
  ctaButtons = [],
}: TableBlockProps) {
  const isAccordionMode = (tables?.length ?? 0) > 1;
  const [openTableIndex, setOpenTableIndex] = useState<number | null>(
    isAccordionMode ? 0 : null
  );

  if (!tables || tables.length === 0) return null;

  const theme = resolveTableTheme(sectionBg);
  const borderClass = getSectionBorderClass(sectionBg);
  const hasHeader = Boolean(headline || tagline || caption);
  const contentPaddingClass = getSectionContentPaddingClass();
  const markdownClassName = cn(ctaMarkdownProseClass, getMarkdownProseClass(sectionBg));
  const hasCtaContent = Boolean(
    showCta &&
      (ctaImage ||
        ctaHeadline ||
        ctaDescription ||
        ctaButtons.length > 0)
  );
  const columnMinWidths = getSharedTableColumnMinWidths(
    tables.map((table) => table.rows)
  );

  return (
    <BlockWrapper bgColor={sectionBgToWrapper(sectionBg)} noPadding>
      <div className="w-full min-w-0">
        {hasHeader && (
          <div
            className={cn(
              getSectionHeaderMobilePaddingClass(),
              "flex flex-col gap-4 border-b",
              "desktop:flex-row desktop:gap-0 desktop:border-b desktop:p-0",
              borderClass
            )}
          >
            {tagline && (
              <div
                className={cn(
                  getSectionTaglineSidebarClass(),
                  "desktop:w-1/3 desktop:border-r",
                  borderClass
                )}
              >
                <SectionTag
                  label={tagline}
                  variant={theme.tagVariant}
                  className="uppercase"
                />
              </div>
            )}

            <div
              className={cn(
                "flex flex-col gap-4",
                tagline ? "desktop:flex-1 desktop:px-10 desktop:py-20" : "desktop:px-10 desktop:py-20"
              )}
            >
              {headline && (
                <H2
                  className={cn(
                    "text-heading-2-sm md:text-heading-2",
                    theme.headline
                  )}
                >
                  {headline}
                </H2>
              )}
              {caption && (
                <MarkdownRenderer
                  content={caption}
                  className={getMarkdownProseClass(sectionBg)}
                />
              )}
            </div>
          </div>
        )}

        <div className="w-full min-w-0">
          {tables.map((table, tableIndex) => {
            const isOpen =
              !isAccordionMode || openTableIndex === tableIndex;

            return (
              <TableBlockTableSection
                key={tableIndex}
                rows={table.rows}
                title={table.title}
                theme={theme}
                borderClass={borderClass}
                columnMinWidths={columnMinWidths}
                isLast={tableIndex === tables.length - 1 && !hasCtaContent}
                accordionMode={isAccordionMode}
                isOpen={isOpen}
                onTitleClick={
                  isAccordionMode
                    ? () => {
                        setOpenTableIndex(isOpen ? null : tableIndex);
                      }
                    : undefined
                }
              />
            );
          })}

          {hasCtaContent && (
            <div
              className={cn(
                "flex flex-col desktop:flex-row desktop:items-stretch",
                borderClass,
                "border-t"
              )}
            >
              {ctaImage && (
                <div
                  className={cn(
                    ctaImageColumnClass,
                    borderClass,
                    "order-2 desktop:order-1",
                    getSectionSplitLeadingClass(sectionBg)
                  )}
                >
                  <Image
                    src={ctaImage.src}
                    alt={ctaImage.alt || ctaHeadline || ""}
                    title={ctaImage.title ?? undefined}
                    fill
                    sizes="(max-width: 767px) 100vw, (max-width: 1439px) 100vw, 33vw"
                    className="object-cover"
                  />
                </div>
              )}

              <div
                className={cn(
                  "order-1 flex w-full flex-col gap-4",
                  contentPaddingClass,
                  "desktop:order-2 desktop:min-h-0",
                  ctaImage && cn(borderClass, "border-b desktop:border-b-0"),
                  ctaImage ? "desktop:w-2/3" : "desktop:w-full"
                )}
              >
                {ctaHeadline && (
                  <H2
                    className={cn(
                      "text-heading-2-sm md:text-heading-2",
                      theme.headline
                    )}
                  >
                    {ctaHeadline}
                  </H2>
                )}
                {ctaDescription && (
                  <MarkdownRenderer
                    content={ctaDescription}
                    className={markdownClassName}
                  />
                )}
                <TableCtaButtonGroup buttons={ctaButtons} />
              </div>
            </div>
          )}
        </div>
      </div>
    </BlockWrapper>
  );
}
